import React, { useState, useEffect, useMemo } from 'react';
import {
  getChallengesAdmin,
  createChallengeAdmin,
  updateChallengeAdmin,
  deleteChallengeAdmin,
  getOrganizationChallengesAdmin,
  getChallengeStatsAdmin,
  duplicateChallengeAdmin,
  bulkUpdateChallengesAdmin,
  exportChallengesAdmin
} from '../services/WebCloudFunctionsService';
import { useTheme } from '../contexts/ThemeContext';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Filter,
  Search,
  Globe,
  Building,
  User,
  Calendar,
  Target,
  Award,
  Activity,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  RefreshCw,
  Sun,
  Moon
} from 'lucide-react';

interface Challenge {
  id: string;
  title: string;
  description: string;
  category: string;
  duration: string;
  emoji: string;
  level: "global" | "organization" | "personal";
  metric: string;
  points: number;
  startDate: string;
  endDate: string;
  targetUnit: string;
  targetValue: number;
  type: string;
  isActive: boolean;
  organizationId?: string;
  allowMultipleEntries?: boolean;
  entryFrequency?: string;
  inputType?: string;
  inputUnit?: string;
  inputValidation?: {
    minValue?: number;
    maxValue?: number;
    step?: number;
  };
  minWalkDistance?: number;
  minWalkDuration?: number;

  // Media content fields
  videoUrl?: string;
  audioUrl?: string;
  imageUrl?: string;

  // Streak bonus fields
  streakBonusEnabled?: boolean;
  streakBonusPoints?: number;
  streakBonusThreshold?: number;
  streakBonusType?: 'daily' | 'weekly' | 'monthly';
  maxStreakBonus?: number;

  // NEW: Measurement target fields
  measurementTarget: number;
  measurementUnit: string;
}

interface ChallengeFormData {
  title: string;
  description: string;
  category: string;
  duration: string;
  emoji: string;
  level: "global" | "organization" | "personal";
  metric: string;
  points: number;
  startDate: string;
  endDate: string;
  targetUnit: string;
  targetValue: number;
  type: string;
  isActive: boolean;
  organizationId: string;
  allowMultipleEntries: boolean;
  inputType: string;
  inputUnit: string;

  // NEW: Measurement target fields
  measurementTarget: number;
  measurementUnit: string;

  // Media content fields
  videoUrl: string;
  audioUrl: string;
  imageUrl: string;

  // Streak bonus fields
  streakBonusEnabled: boolean;
  streakBonusPoints: number;
  streakBonusThreshold: number;
  streakBonusType: 'daily' | 'weekly' | 'monthly';
  maxStreakBonus: number;
}

const Challenges: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [challenges, setChallenges] = useState<Challenge[]>([]);

  // Debug theme state
  useEffect(() => {
    console.log('🎨 Admin Challenges - Dark mode:', isDarkMode);
    console.log('🎨 Document data-theme:', document.documentElement.getAttribute('data-theme'));
  }, [isDarkMode]);
  const [filteredChallenges, setFilteredChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [challengeStats, setChallengeStats] = useState<any>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState<ChallengeFormData>({
    title: '',
    description: '',
    category: 'daily',
    duration: 'daily',
    emoji: '🎯',
    level: 'personal',
    metric: 'selfReported',
    points: 100,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    targetUnit: 'times',
    targetValue: 1,
    type: 'wellness',
    isActive: true,
    organizationId: '',
    allowMultipleEntries: false,
    inputType: 'checkbox',
    inputUnit: '',

    // NEW: Initialize measurement fields
    measurementTarget: 1,
    measurementUnit: 'times',

    videoUrl: '',
    audioUrl: '',
    imageUrl: '',
    streakBonusEnabled: false,
    streakBonusPoints: 0,
    streakBonusThreshold: 7,
    streakBonusType: 'daily',
    maxStreakBonus: 50
  });

  // Emoji categories and data
  const emojiCategories = {
    popular: ['🎯', '🏆', '⭐', '🔥', '💪', '🚀', '🎉', '💎'],
    fitness: ['🏃', '🚶', '🏋️', '🧘', '🏊', '🚴', '⚽', '🏀', '🎾', '🏓', '🥊', '🥋', '🏆', '💪', '🔥', '💯'],
    nature: ['🌲', '🏔️', '🌊', '🌅', '🌙', '⭐', '🌈', '🌸', '🍃', '🌿', '🌱', '🌳', '🏕️', '⛰️', '🏖️', '🌺'],
    food: ['🥗', '🥑', '🥝', '🍎', '🥕', '🥦', '🥜', '🌰', '🥛', '🥚', '🐟', '🍗', '🥩', '🍞', '🍚', '🥣'],
    wellness: ['🧘', '🧠', '💆', '🛀', '🕯️', '🌸', '🌿', '💧', '☕', '🍵', '🫖', '🕉️', '☮️', '✌️', '🙏', '💝'],
    social: ['👥', '🤝', '👫', '👭', '👬', '👨‍👩‍👧‍👦', '🏘️', '🏢', '🏫', '🏥', '🏪', '🎭', '🎪', '🎨', '🎤', '🎬'],
    achievement: ['🎯', '🏆', '🥇', '🥈', '🥉', '⭐', '💎', '👑', '🏅', '🎖️', '💪', '🔥', '💯', '🚀', '⚡', '🌟'],
    time: ['⏰', '📅', '🗓️', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🌅'],
    fun: ['🎉', '🎊', '🎈', '🎂', '🎁', '🎄', '🎃', '👻', '🦄', '🌈', '✨', '💫', '🎭', '🎪', '🎨', '🎬']
  };

  // Filter emojis based on search
  const filteredEmojis = useMemo(() => {
    if (!emojiSearch) return emojiCategories;

    const searchLower = emojiSearch.toLowerCase();
    const filtered: Record<string, string[]> = {};

    Object.entries(emojiCategories).forEach(([category, emojis]) => {
      const filteredEmojis = emojis.filter(emoji =>
        emoji.includes(searchLower) ||
        category.includes(searchLower)
      );
      if (filteredEmojis.length > 0) {
        filtered[category] = filteredEmojis;
      }
    });

    return filtered;
  }, [emojiSearch]);

  useEffect(() => {
    fetchChallenges();
  }, []);

  useEffect(() => {
    filterChallenges();
  }, [challenges, searchTerm, levelFilter, statusFilter, organizationFilter]);

  useEffect(() => {
    // Close emoji picker when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showEmojiPicker && !target.closest('.emoji-picker-container')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getChallengesAdmin() as { challenges: Challenge[] };

      if (result && result.challenges && Array.isArray(result.challenges)) {
        setChallenges(result.challenges);
      } else {
        setChallenges([]);
      }
    } catch (err: any) {
      console.error('Error fetching challenges:', err);
      setError(err?.message || 'Failed to load challenges. Please try again.');
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  };

  const filterChallenges = () => {
    let filtered = challenges;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(challenge =>
        challenge.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        challenge.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(challenge => challenge.level === levelFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(challenge => challenge.isActive === isActive);
    }

    // Organization filter
    if (organizationFilter !== 'all') {
      filtered = filtered.filter(challenge => challenge.organizationId === organizationFilter);
    }

    setFilteredChallenges(filtered);
  };

  const handleCreateChallenge = async () => {
    try {
      // Validate required fields based on challenge level
      if (formData.level === 'organization' && !formData.organizationId?.trim()) {
        setError('Organization ID is required for organization challenges');
        return;
      }

      // Validate measurement fields
      if (!formData.measurementTarget || formData.measurementTarget < 0.1) {
        setError('Measurement target must be at least 0.1');
        return;
      }

      if (!formData.measurementUnit) {
        setError('Measurement unit is required');
        return;
      }

      // Validate measurement target based on metric type
      const validationError = validateMeasurementTarget(formData.measurementTarget, formData.metric);
      if (validationError) {
        setError(validationError);
        return;
      }

      const challengeGroup = `${formData.level}_challenges`;

      // Convert date strings to ISO format and set proper values
      const challengeData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        // Use measurement target for what users need to achieve
        targetValue: formData.measurementTarget,
        // Use measurement unit for the input field
        targetUnit: formData.measurementUnit,
        // Ensure organization ID is properly set
        organizationId: formData.level === 'organization' ? formData.organizationId.trim() : undefined
      };

      console.log('Creating challenge with data:', challengeData);
      await createChallengeAdmin(challengeData, challengeGroup);
      setShowCreateModal(false);
      resetForm();
      fetchChallenges();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateChallenge = async () => {
    if (!selectedChallenge) return;

    try {
      // Validate measurement fields
      if (!formData.measurementTarget || formData.measurementTarget < 0.1) {
        setError('Measurement target must be at least 0.1');
        return;
      }

      if (!formData.measurementUnit) {
        setError('Measurement unit is required');
        return;
      }

      // Validate measurement target based on metric type
      const validationError = validateMeasurementTarget(formData.measurementTarget, formData.metric);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Use the form data level to determine the challenge group, not the selected challenge level
      // This ensures that if the level is being changed, we use the new level
      const challengeGroup = `${formData.level}_challenges`;

      // Convert date strings to ISO format and set proper values
      const challengeData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        // Use measurement target for what users need to achieve
        targetValue: formData.measurementTarget,
        // Use measurement unit for the input field
        targetUnit: formData.measurementUnit
      };

      const result = await updateChallengeAdmin(selectedChallenge.id, challengeGroup, challengeData);

      // Check if the challenge was migrated to a different group
      if (result && typeof result === 'object' && 'challengeGroup' in result && result.challengeGroup !== challengeGroup) {
        console.log(`🔄 Challenge migrated from ${challengeGroup} to ${result.challengeGroup}`);
        // Update the selected challenge's level to reflect the new group
        selectedChallenge.level = (result.challengeGroup as string).replace('_challenges', '') as "global" | "organization" | "personal";

        // If the challenge ID changed (e.g., when migrating to organization), update it
        const resultData = result as any;
        if (resultData.challengeId && resultData.challengeId !== selectedChallenge.id) {
          console.log(`🆔 Challenge ID changed from ${selectedChallenge.id} to ${resultData.challengeId}`);
          selectedChallenge.id = resultData.challengeId;
        }
      }

      setShowEditModal(false);
      resetForm();
      fetchChallenges();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteChallenge = async () => {
    if (!selectedChallenge) return;

    try {
      // Try to delete from the current level first
      // Use the form data level if we're in edit mode, otherwise use the selected challenge level
      let challengeGroup = showEditModal ? `${formData.level}_challenges` : `${selectedChallenge.level}_challenges`;

      try {
        await deleteChallengeAdmin(selectedChallenge.id, challengeGroup);
      } catch (err: any) {
        // If deletion fails, the challenge might have been migrated
        // Try to find it in other groups
        const allGroups = ['global_challenges', 'organization_challenges', 'personal_challenges'];
        let deleted = false;

        for (const group of allGroups) {
          if (group !== challengeGroup) {
            try {
              await deleteChallengeAdmin(selectedChallenge.id, group);
              deleted = true;
              console.log(`🔄 Challenge ${selectedChallenge.id} found and deleted from ${group}`);
              break;
            } catch (groupErr) {
              // Continue to next group
            }
          }
        }

        if (!deleted) {
          // If we still can't find it, throw the original error
          throw err;
        }
      }

      setShowDeleteModal(false);
      setSelectedChallenge(null);
      fetchChallenges();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleBulkUpdate = async (updates: { isActive?: boolean; points?: number; targetValue?: number }) => {
    if (selectedChallenges.length === 0) return;

    try {
      setLoading(true);
      await bulkUpdateChallengesAdmin({
        challengeIds: selectedChallenges,
        updates
      });
      await fetchChallenges();
      setSelectedChallenges([]);
      setShowBulkActions(false);
    } catch (err: any) {
      setError(err.message || 'Failed to bulk update challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateChallenge = async (challengeId: string, newData: { title: string; startDate: string; endDate: string }) => {
    try {
      setLoading(true);
      await duplicateChallengeAdmin(challengeId, newData);
      await fetchChallenges();
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate challenge');
    } finally {
      setLoading(false);
    }
  };

  const handleExportChallenges = async (format: 'csv' | 'json' = 'csv') => {
    try {
      setLoading(true);
      const filters = {
        level: levelFilter !== 'all' ? levelFilter : undefined,
        organizationId: organizationFilter !== 'all' ? organizationFilter : undefined,
        isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
        format
      };

      const exportData = await exportChallengesAdmin(filters) as any;

      // Create and download file
      const blob = new Blob([format === 'csv' ? exportData.csv : JSON.stringify(exportData, null, 2)], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `challenges_export.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStats = async (challengeId?: string) => {
    try {
      setLoading(true);
      const stats = await getChallengeStatsAdmin(challengeId);
      setChallengeStats(stats);
      setShowStatsModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load challenge statistics');
    } finally {
      setLoading(false);
    }
  };

  const toggleChallengeSelection = (challengeId: string) => {
    setSelectedChallenges(prev =>
      prev.includes(challengeId)
        ? prev.filter(id => id !== challengeId)
        : [...prev, challengeId]
    );
  };

  const selectAllChallenges = () => {
    setSelectedChallenges(filteredChallenges.map(c => c.id));
  };

  const deselectAllChallenges = () => {
    setSelectedChallenges([]);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'daily',
      duration: 'daily',
      emoji: '🎯',
      level: 'personal',
      metric: 'selfReported',
      points: 100,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      targetUnit: 'times',
      targetValue: 1,
      type: 'wellness',
      isActive: true,
      organizationId: '',
      allowMultipleEntries: false,
      inputType: 'checkbox',
      inputUnit: '',

      // NEW: Initialize measurement fields
      measurementTarget: 1,
      measurementUnit: 'times',

      videoUrl: '',
      audioUrl: '',
      imageUrl: '',
      streakBonusEnabled: false,
      streakBonusPoints: 0,
      streakBonusThreshold: 7,
      streakBonusType: 'daily',
      maxStreakBonus: 50
    });
    setError(null);
  };

  const openEditModal = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setFormData({
      title: challenge.title,
      description: challenge.description,
      category: challenge.category,
      duration: challenge.duration,
      emoji: challenge.emoji,
      level: challenge.level,
      metric: challenge.metric,
      points: challenge.points,
      startDate: challenge.startDate ? new Date(challenge.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      endDate: challenge.endDate ? new Date(challenge.endDate).toISOString().split('T')[0] : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      targetUnit: challenge.targetUnit,
      targetValue: challenge.targetValue,
      type: challenge.type,
      isActive: challenge.isActive,
      organizationId: challenge.organizationId || '',
      allowMultipleEntries: challenge.allowMultipleEntries || false,
      inputType: challenge.inputType || 'checkbox',
      inputUnit: challenge.inputUnit || '',

      // NEW: Load measurement fields from existing challenge
      measurementTarget: challenge.measurementTarget || challenge.targetValue || 1,
      measurementUnit: challenge.measurementUnit || challenge.targetUnit || 'times',

      videoUrl: challenge.videoUrl || '',
      audioUrl: challenge.audioUrl || '',
      imageUrl: challenge.imageUrl || '',
      streakBonusEnabled: challenge.streakBonusEnabled || false,
      streakBonusPoints: challenge.streakBonusPoints || 0,
      streakBonusThreshold: challenge.streakBonusThreshold || 7,
      streakBonusType: challenge.streakBonusType || 'daily',
      maxStreakBonus: challenge.maxStreakBonus || 50
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setShowDeleteModal(true);
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'global': return <Globe className="w-4 h-4" />;
      case 'organization': return <Building className="w-4 h-4" />;
      case 'personal': return <User className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const handleLevelChange = (level: string) => {
    setFormData(prev => ({
      ...prev,
      level: level as "global" | "organization" | "personal",
      // Reset organization ID when switching away from organization level
      organizationId: level === 'organization' ? prev.organizationId : ''
    }));
  };

  const handleDurationChange = (duration: string) => {
    setFormData(prev => ({
      ...prev,
      duration: duration,
      // Auto-update points and target when duration changes
      points: calculateSmartPoints(),
      targetValue: calculateSmartTarget(),
      targetUnit: calculateSmartTargetUnit()
    }));
  };

  // Handle date changes to recalculate target
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Recalculate target when dates change
      targetValue: calculateSmartTarget()
    }));
  };

  // Handle points changes to recalculate target
  const handlePointsChange = (value: number) => {
    setFormData(prev => ({
      ...prev,
      points: value,
      // Don't recalculate target - let users set their own values
    }));
  };

  // Smart points calculation helper
  const calculateSmartPoints = () => {
    // Use the new fixed points system
    switch (formData.duration) {
      case 'daily':
        return 10; // 10 points for daily challenges
      case 'weekly':
        return 100; // 100 points for weekly challenges
      case 'monthly':
        return 500; // 500 points for monthly challenges
      default:
        return 100; // Default to 100 points
    }
  };

  // Update the smart target calculation to use measurement target instead of points
  const calculateSmartTarget = () => {
    if (!formData.startDate || !formData.endDate) return 1;

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // For daily challenges, target is 1 completion per day
    if (formData.duration === 'daily') {
      return 1;
    }
    // For weekly challenges, target is 1 completion per week
    else if (formData.duration === 'weekly') {
      return Math.ceil(totalDays / 7);
    }
    // For monthly challenges, target is 1 completion per month
    else if (formData.duration === 'monthly') {
      return Math.ceil(totalDays / 30);
    }

    return 1;
  };

  // Smart target unit helper
  const calculateSmartTargetUnit = () => {
    return 'points'; // Target is now total points, not times
  };

  const applySmartPoints = () => {
    const suggestedPoints = calculateSmartPoints();
    const suggestedTarget = calculateSmartTarget();
    const suggestedTargetUnit = calculateSmartTargetUnit();

    setFormData(prev => ({
      ...prev,
      points: suggestedPoints,
      targetValue: suggestedTarget,
      targetUnit: suggestedTargetUnit
    }));
  };

  // NEW: Calculate total points based on measurement target and duration
  const calculateTotalPoints = () => {
    if (!formData.startDate || !formData.endDate) return formData.points;

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Total points = points per completion × number of possible completions
    return formData.points * Math.ceil(totalDays / getCompletionFrequency());
  };

  // NEW: Helper to get completion frequency based on duration
  const getCompletionFrequency = () => {
    switch (formData.duration) {
      case 'daily': return 1;
      case 'weekly': return 7;
      case 'monthly': return 30;
      default: return 1;
    }
  };

  // Handle metric changes to auto-set measurement unit
  const handleMetricChange = (metric: string) => {
    const defaultUnit = getMeasurementUnits(metric)[0] || 'times';

    setFormData(prev => ({
      ...prev,
      metric,
      // Auto-set measurement unit based on metric type
      measurementUnit: defaultUnit,
      // Don't auto-set measurement target - let users enter their own values
    }));

    // Clear any existing errors
    setError(null);
  };

  // Get default measurement target based on metric type
  const getDefaultMeasurementTarget = (metric: string): number => {
    switch (metric) {
      case 'steps':
        return 5000; // Default to 5000 steps
      case 'time':
        return 30; // Default to 30 minutes
      case 'selfReported':
        // Return default based on challenge type for better context
        return getDefaultTargetByChallengeType();
      default:
        return 1;
    }
  };

  // Get default measurement target based on challenge type
  const getDefaultTargetByChallengeType = (type?: string): number => {
    const challengeType = type || formData.type;
    switch (challengeType) {
      case 'walking':
        return 5000; // 5000 steps
      case 'wellness':
        return 1; // 1 session
      case 'nutrition':
        return 3; // 3 meals
      case 'hydration':
        return 64; // 64 fl oz (8 cups)
      case 'social':
        return 3; // 3 interactions
      case 'fitness':
        return 10; // 10 reps
      case 'meditation':
        return 10; // 10 minutes
      case 'reading':
        return 20; // 20 pages
      case 'learning':
        return 1; // 1 lesson
      case 'creativity':
        return 1; // 1 project
      case 'productivity':
        return 3; // 3 tasks
      default:
        return 1;
    }
  };

  // Get appropriate measurement units based on metric type
  const getMeasurementUnits = (metric?: string) => {
    const metricType = metric || formData.metric;
    const challengeTypeUnits = getUnitsByChallengeType();

    switch (metricType) {
      case 'steps':
        // For steps, include both step units and challenge type-specific units
        const stepUnits = ['steps', 'kilometers', 'miles', 'meters'];
        // Combine and remove duplicates
        return Array.from(new Set([...stepUnits, ...challengeTypeUnits]));
      case 'time':
        // For time, include both time units and challenge type-specific units
        const timeUnits = ['minutes', 'hours', 'seconds'];
        // Combine and remove duplicates
        return Array.from(new Set([...timeUnits, ...challengeTypeUnits]));
      case 'selfReported':
        // Return units based on challenge type for better context
        return challengeTypeUnits;
      default:
        // Always include challenge type units plus some defaults
        return Array.from(new Set([...challengeTypeUnits, 'times', 'units', 'items']));
    }
  };

  // Get measurement units based on challenge type for better context
  const getUnitsByChallengeType = (type?: string) => {
    const challengeType = type || formData.type;
    switch (challengeType) {
      case 'walking':
        return ['steps', 'kilometers', 'miles', 'meters', 'times'];
      case 'wellness':
        return ['times', 'sessions', 'minutes', 'check-ins', 'activities'];
      case 'nutrition':
        return ['meals', 'servings', 'glasses', 'fl oz', 'cups', 'ounces', 'grams', 'calories', 'times', 'items'];
      case 'hydration':
        return ['fl oz', 'glasses', 'cups', 'liters', 'bottles', 'ounces', 'ml', 'times'];
      case 'social':
        return ['interactions', 'conversations', 'meetings', 'calls', 'messages', 'times'];
      case 'fitness':
        return ['reps', 'sets', 'minutes', 'miles', 'kilometers', 'pounds', 'kg', 'calories', 'times'];
      case 'meditation':
        return ['minutes', 'sessions', 'times', 'days'];
      case 'reading':
        return ['pages', 'chapters', 'books', 'minutes', 'times'];
      case 'learning':
        return ['lessons', 'modules', 'hours', 'topics', 'times'];
      case 'creativity':
        return ['projects', 'pieces', 'hours', 'sessions', 'times'];
      case 'productivity':
        return ['tasks', 'hours', 'projects', 'goals', 'times'];
      default:
        return ['times', 'units', 'items', 'check-ins'];
    }
  };

  // Get suggested range for measurement target based on metric type
  const getSuggestedRange = (): string => {
    switch (formData.metric) {
      case 'steps':
        return '100 - 50,000 steps (typical daily range)';
      case 'time':
        return '1 - 180 minutes (1 minute to 3 hours)';
      case 'selfReported':
        // Return range based on challenge type for better context
        return getSuggestedRangeByChallengeType();
      default:
        return '0.1 - 100 (flexible range)';
    }
  };

  // Get suggested range based on challenge type
  const getSuggestedRangeByChallengeType = (type?: string) => {
    const challengeType = type || formData.type;
    switch (challengeType) {
      case 'walking':
        return '1,000 - 25,000 steps (realistic daily range)';
      case 'wellness':
        return '1 - 5 sessions (daily wellness activities)';
      case 'nutrition':
        return '1 - 6 meals/servings (daily nutrition goals)';
      case 'hydration':
        return '16 - 128 fl oz (2-16 cups daily)';
      case 'social':
        return '1 - 10 interactions (daily social engagement)';
      case 'fitness':
        return '5 - 100 reps (workout intensity)';
      case 'meditation':
        return '5 - 60 minutes (meditation sessions)';
      case 'reading':
        return '5 - 100 pages (reading progress)';
      case 'learning':
        return '1 - 5 lessons (learning pace)';
      case 'creativity':
        return '1 - 5 projects (creative output)';
      case 'productivity':
        return '1 - 10 tasks (daily productivity)';
      default:
        return '1 - 100 (flexible range)';
    }
  };

  // Get example text based on metric type
  const getExampleText = (): string => {
    switch (formData.metric) {
      case 'steps':
        return `Walk ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'time':
        return `Exercise for ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'selfReported':
        // Return example based on challenge type for better context
        return getExampleTextByChallengeType();
      default:
        return `Achieve ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
    }
  };

  // Get example text based on challenge type
  const getExampleTextByChallengeType = (type?: string) => {
    const challengeType = type || formData.type;
    switch (challengeType) {
      case 'walking':
        return `Walk ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'wellness':
        return `Complete ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'nutrition':
        return `Eat ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'hydration':
        return `Drink ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'social':
        return `Have ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'fitness':
        return `Complete ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'meditation':
        return `Meditate for ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'reading':
        return `Read ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'learning':
        return `Complete ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'creativity':
        return `Create ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      case 'productivity':
        return `Complete ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
      default:
        return `Complete ${formData.measurementTarget} ${formData.measurementUnit} to earn ${formData.points} points`;
    }
  };

  // Validate measurement target based on metric type
  const validateMeasurementTarget = (target: number, metric: string): string | null => {
    if (target <= 0) return 'Target must be greater than 0';

    switch (metric) {
      case 'steps':
        if (target > 100000) return 'Step target seems too high (max 100,000)';
        if (target < 100) return 'Step target seems too low (min 100)';
        break;
      case 'time':
        if (target > 1440) return 'Time target seems too high (max 24 hours = 1440 minutes)';
        if (target < 1) return 'Time target seems too low (min 1 minute)';
        break;
      case 'selfReported':
        // Validate based on challenge type for better context
        return validateTargetByChallengeType(target, formData.type);
      default:
        if (target > 1000) return 'Target seems too high (max 1000)';
        if (target < 0.1) return 'Target must be at least 0.1';
    }
    return null;
  };

  // Validate measurement target based on challenge type
  const validateTargetByChallengeType = (target: number, type?: string): string | null => {
    const challengeType = type || formData.type;
    switch (challengeType) {
      case 'walking':
        if (target > 100000) return 'Step target seems too high (max 100,000)';
        if (target < 100) return 'Step target seems too low (min 100)';
        break;
      case 'wellness':
        if (target > 100) return 'Wellness target seems too high (max 100 per day)';
        if (target < 1) return 'Wellness target must be at least 1';
        break;
      case 'nutrition':
        if (target > 50) return 'Nutrition target seems too high (max 50 per day)';
        if (target < 1) return 'Nutrition target must be at least 1';
        break;
      case 'hydration':
        if (target > 500) return 'Hydration target seems too high (max 500 fl oz)';
        if (target < 1) return 'Hydration target must be at least 1 fl oz';
        break;
      case 'social':
        if (target > 200) return 'Social interactions seem too high (max 200 per day)';
        if (target < 1) return 'Social interactions must be at least 1';
        break;
      case 'fitness':
        if (target > 10000) return 'Fitness reps seem too high (max 10,000)';
        if (target < 1) return 'Fitness reps must be at least 1';
        break;
      case 'meditation':
        if (target > 480) return 'Meditation time seems too high (max 8 hours)';
        if (target < 1) return 'Meditation time must be at least 1 minute';
        break;
      case 'reading':
        if (target > 1000) return 'Reading pages seem too high (max 1,000 per day)';
        if (target < 1) return 'Reading pages must be at least 1';
        break;
      case 'learning':
        if (target > 100) return 'Learning lessons seem too high (max 100 per day)';
        if (target < 1) return 'Learning lessons must be at least 1';
        break;
      case 'creativity':
        if (target > 100) return 'Creative projects seem too high (max 100 per day)';
        if (target < 1) return 'Creative projects must be at least 1';
        break;
      case 'productivity':
        if (target > 200) return 'Productivity tasks seem too high (max 200 per day)';
        if (target < 1) return 'Productivity tasks must be at least 1';
        break;
      default:
        if (target > 10000) return 'Target seems too high (max 10,000)';
        if (target < 0.1) return 'Target must be at least 0.1';
    }
    return null;
  };

  // Handle measurement target changes with validation
  const handleMeasurementTargetChange = (value: number) => {
    const validationError = validateMeasurementTarget(value, formData.metric);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFormData(prev => ({
      ...prev,
      measurementTarget: value
    }));
    setError(null);
  };

  // Handle challenge type changes to auto-set appropriate measurement units and targets
  const handleChallengeTypeChange = (type: string) => {
    const defaultUnit = getUnitsByChallengeType(type)[0] || 'times';

    // Auto-set metric to 'selfReported' for challenge types that need specific units
    let autoMetric = formData.metric;
    if (['hydration', 'nutrition', 'social', 'meditation', 'reading', 'learning', 'creativity', 'productivity'].includes(type)) {
      autoMetric = 'selfReported';
    }

    setFormData(prev => ({
      ...prev,
      type,
      metric: autoMetric,
      // Auto-set measurement unit based on challenge type
      measurementUnit: defaultUnit,
      // Don't auto-set measurement target or points - let users enter their own values
    }));

    // Clear any existing errors
    setError(null);
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center py-10">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-slate-600">Loading challenges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Challenge Management</h1>
              <p className="mt-1 text-slate-600 dark:text-slate-300">Manage global, organization, and personal challenges</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleTheme}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {isDarkMode ? '🌙 Dark' : '☀️ Light'}
              </span>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Challenge
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 mb-6 transition-colors duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search challenges..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Level Filter */}
            <div>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Levels</option>
                <option value="global">Global</option>
                <option value="organization">Organization</option>
                <option value="personal">Personal</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Organization Filter */}
            <div>
              <select
                value={organizationFilter}
                onChange={(e) => setOrganizationFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Organizations</option>
                <option value="uh">UH</option>
                <option value="org1">Organization 1</option>
                <option value="org2">Organization 2</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <XCircle className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredChallenges.map((challenge) => (
            <div
              key={challenge.id}
              className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all duration-300"
            >
              {/* Challenge Header */}
              <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{challenge.emoji}</span>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-50 line-clamp-1">
                        {challenge.title || (challenge as any).name || challenge.id || 'Untitled Challenge'}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        {getLevelIcon(challenge.level)}
                        <span className="text-sm text-slate-500 dark:text-slate-400 capitalize">{challenge.level || 'unknown'}</span>
                        {getStatusBadge(challenge.isActive)}
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <button className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
                      <MoreHorizontal className="w-4 h-4 text-slate-400 dark:text-slate-300" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Challenge Content */}
              <div className="p-6">
                <p className="text-slate-600 dark:text-slate-300 text-sm line-clamp-2 mb-4">{challenge.description}</p>

                <div className="space-y-3">
                  {/* Target */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Target</span>
                    <div className="flex items-center space-x-1">
                      <Target className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-slate-900 dark:text-slate-50">
                        {challenge.targetValue} {challenge.targetUnit}
                      </span>
                    </div>
                  </div>

                  {/* Points */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Points</span>
                    <div className="flex items-center space-x-1">
                      <Award className="w-4 h-4 text-yellow-500" />
                      <span className="font-medium text-slate-900 dark:text-slate-50">
                        {challenge.points} per completion
                      </span>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Duration</span>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4 text-green-500" />
                      <span className="font-medium text-slate-900 dark:text-slate-50 capitalize">{challenge.duration}</span>
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Start Date</span>
                    <span className="font-medium text-slate-900 dark:text-slate-50">
                      {challenge.startDate ? new Date(challenge.startDate).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>

                  {/* End Date */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">End Date</span>
                    <span className="font-medium text-slate-900 dark:text-slate-50">
                      {challenge.endDate ? new Date(challenge.endDate).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>

                  {/* Organization */}
                  {challenge.organizationId && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Organization</span>
                      <span className="font-medium text-slate-900 dark:text-slate-50">{challenge.organizationId}</span>
                    </div>
                  )}

                  {/* Media Content Indicators */}
                  {(challenge.videoUrl || challenge.audioUrl || challenge.imageUrl) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Media</span>
                      <div className="flex items-center space-x-2">
                        {challenge.videoUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            🎥 Video
                          </span>
                        )}
                        {challenge.audioUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🎵 Audio
                          </span>
                        )}
                        {challenge.imageUrl && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            🖼️ Image
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Streak Bonus Indicators */}
                  {challenge.streakBonusEnabled && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Streak Bonus</span>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          🏆 {challenge.streakBonusType} +{challenge.streakBonusPoints}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Max: {challenge.maxStreakBonus}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Challenge Actions */}
              <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 rounded-b-xl border-t border-slate-100 dark:border-slate-800 transition-colors duration-300">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                      {challenge.category} • {challenge.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(challenge)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => openDeleteModal(challenge)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-red-600 px-4 py-1.5 text-xs font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredChallenges.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
              <Activity className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No challenges found</h3>
            <p className="text-slate-600 mb-6">Try adjusting your filters or create a new challenge.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Challenge
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 rounded-t-2xl border-b border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-white text-xl">🎯</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {showCreateModal ? 'Create New Challenge' : 'Edit Challenge'}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {showCreateModal ? 'Set up an engaging challenge for your users' : 'Update challenge settings and configuration'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Challenge Rules Explanation */}
              <div className="mb-8 border border-blue-200 dark:border-blue-700 rounded-xl overflow-hidden">
                {/* Rules Header with Toggle */}
                <button
                  onClick={() => setShowRules(!showRules)}
                  className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/30 dark:hover:to-indigo-800/30 transition-all duration-200 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">📋</span>
                    <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                      Challenge Creation Rules
                    </h3>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                      {showRules ? 'Hide Rules' : 'Show Rules'}
                    </span>
                    <span className={`text-blue-600 dark:text-blue-400 transition-transform duration-200 ${showRules ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </div>
                </button>

                {/* Collapsible Rules Content */}
                {showRules && (
                  <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-700">
                    {/* Points System */}
                    <div className="mb-6">
                      <h4 className="text-base font-semibold text-blue-800 dark:text-blue-200 mb-3">🔹 Points System (Fixed & Easy to Understand)</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="border-b border-blue-300 dark:border-blue-600">
                              <th className="text-left py-2 px-3 font-semibold text-blue-800 dark:text-blue-200">Goal Type</th>
                              <th className="text-left py-2 px-3 font-semibold text-blue-800 dark:text-blue-200">Points for Completion</th>
                              <th className="text-left py-2 px-3 font-semibold text-blue-800 dark:text-blue-200">Bonus for Consistency</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-blue-200 dark:border-blue-700">
                              <td className="py-2 px-3 font-medium">Daily</td>
                              <td className="py-2 px-3">10 pts each</td>
                              <td className="py-2 px-3">+5 pts if 7-day streak</td>
                            </tr>
                            <tr className="border-b border-blue-200 dark:border-blue-700">
                              <td className="py-2 px-3 font-medium">Weekly</td>
                              <td className="py-2 px-3">100 pts each</td>
                              <td className="py-2 px-3">+20 pts for consecutive weeks</td>
                            </tr>
                            <tr>
                              <td className="py-2 px-3 font-medium">Monthly</td>
                              <td className="py-2 px-3">500 pts each</td>
                              <td className="py-2 px-3">+100 pts if all daily goals were met</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Example Scenarios */}
                    <div className="mb-6">
                      <h4 className="text-base font-semibold text-blue-800 dark:text-blue-200 mb-3">🔹 Example Scenarios</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="bg-blue-100 dark:bg-blue-800/30 p-3 rounded-lg">
                          <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Daily Wins</h5>
                          <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                            <li>• Complete 1 daily task = 10 pts</li>
                            <li>• Do it every day for 7 days = 70 pts + 5 bonus = 75 pts</li>
                          </ul>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-800/30 p-3 rounded-lg">
                          <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Weekly Wins</h5>
                          <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                            <li>• Meet your weekly goal = 100 pts</li>
                            <li>• Do it 2 weeks in a row = 200 pts + 20 bonus = 220 pts</li>
                          </ul>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-800/30 p-3 rounded-lg">
                          <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Monthly Wins</h5>
                          <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                            <li>• Hit your monthly milestone = 500 pts</li>
                            <li>• Never missed daily goals = 500 + 100 bonus = 600 pts</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Why It Works */}
                    <div>
                      <h4 className="text-base font-semibold text-blue-800 dark:text-blue-200 mb-2">🔹 Why It Works</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-blue-300">
                        <div>
                          <strong>Daily points →</strong> encourage consistency.
                        </div>
                        <div>
                          <strong>Weekly points →</strong> reward short-term milestones.
                        </div>
                        <div>
                          <strong>Monthly points →</strong> give big payoff for long-term discipline.
                        </div>
                        <div>
                          <strong>Bonuses →</strong> keep players motivated by streaks.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center mr-3">
                      📝
                    </span>
                    Basic Information
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Challenge Title
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="Enter an engaging challenge title"
                        />
                        {!formData.title && (
                          <p className="text-red-500 text-sm mt-1">Title is required</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Description
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                          placeholder="Describe what users need to accomplish"
                        />
                        {!formData.description && (
                          <p className="text-red-500 text-sm mt-1">Description is required</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Emoji
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 flex items-center justify-between"
                          >
                            <span className="text-2xl">{formData.emoji || '🎯'}</span>
                            <span className="text-gray-500 text-sm">Choose emoji</span>
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {/* Emoji Picker Dropdown */}
                          {showEmojiPicker && (
                            <div className="emoji-picker-container absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-2xl z-50 max-h-80 overflow-y-auto">
                              {/* Search Bar */}
                              <div className="sticky top-0 p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-t-xl">
                                <input
                                  type="text"
                                  placeholder="Search emojis..."
                                  value={emojiSearch}
                                  onChange={(e) => setEmojiSearch(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              {/* Emoji Categories */}
                              <div className="p-3">
                                {/* Search Results or Categories */}
                                {emojiSearch ? (
                                  // Show search results
                                  <div className="mb-4">
                                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                      Search Results for "{emojiSearch}"
                                    </h4>
                                    <div className="grid grid-cols-8 gap-2">
                                      {Object.values(filteredEmojis).flat().map((emoji: string, index: number) => (
                                        <button
                                          key={`${emoji}-${index}`}
                                          type="button"
                                          onClick={() => {
                                            setFormData({ ...formData, emoji });
                                            setShowEmojiPicker(false);
                                          }}
                                          className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  // Show categories
                                  <>
                                    {/* General */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">General</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.popular.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Fitness & Health */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Fitness & Health</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.fitness.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Nature & Outdoors */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Nature & Outdoors</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.nature.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Food & Nutrition */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Food & Nutrition</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.food.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Wellness & Mind */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Wellness & Mind</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.wellness.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Social & Community */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Social & Community</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.social.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Achievement & Goals */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Achievement & Goals</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.achievement.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Time & Schedule */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Time & Schedule</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.time.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Fun & Celebration */}
                                    <div className="mb-4">
                                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Fun & Celebration</h4>
                                      <div className="grid grid-cols-8 gap-2">
                                        {emojiCategories.fun.map((emoji: string) => (
                                          <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                              setFormData({ ...formData, emoji });
                                              setShowEmojiPicker(false);
                                            }}
                                            className="w-10 h-10 text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {!formData.emoji && (
                          <p className="text-red-500 text-sm mt-1">Emoji is required</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Challenge Level
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                          value={formData.level}
                          onChange={(e) => handleLevelChange(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        >
                          <option value="global">🌍 Global</option>
                          <option value="organization">🏢 Organization</option>
                          <option value="personal">👤 Personal</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Challenge Type
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) => handleChallengeTypeChange(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        >
                          <option value="walking">🚶 Walking</option>
                          <option value="wellness">🧘 Wellness</option>
                          <option value="nutrition">🥗 Nutrition</option>
                          <option value="hydration">💧 Hydration</option>
                          <option value="social">👥 Social</option>
                          <option value="fitness">💪 Fitness</option>
                          <option value="meditation">🧘‍♀️ Meditation</option>
                          <option value="reading">📚 Reading</option>
                          <option value="learning">🎓 Learning</option>
                          <option value="creativity">🎨 Creativity</option>
                          <option value="productivity">⚡ Productivity</option>
                        </select>
                      </div>
                    </div>

                    {/* Organization ID - Only show when organization level is selected */}
                    {formData.level === 'organization' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Organization ID
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.organizationId}
                          onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="Enter organization identifier"
                        />
                        {!formData.organizationId && (
                          <p className="text-red-500 text-sm mt-1">Organization ID is required</p>
                        )}
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Quick select:</p>
                          <div className="flex flex-wrap gap-2">
                            {['uh', 'org1', 'org2', 'individual'].map(orgId => (
                              <button
                                key={orgId}
                                type="button"
                                onClick={() => setFormData({ ...formData, organizationId: orgId })}
                                className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-300 dark:border-blue-700 transition-colors"
                              >
                                {orgId}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Challenge Configuration */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center mr-3">
                      ⚙️
                    </span>
                    Challenge Configuration
                  </h3>

                  <div className="space-y-4">
                    {/* Challenge Frequency - This drives the date logic */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Challenge Frequency
                        <span className="text-red-500 ml-1">*</span>
                        <span className="ml-2 text-gray-500 text-xs">📅 How often can users complete this?</span>
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                      >
                        <option value="daily">📅 Daily - Ongoing challenge, earn points every day</option>
                        <option value="weekly">📊 Weekly - Complete weekly goals within time period</option>
                        <option value="monthly">📈 Monthly - Complete monthly goals within time period</option>
                      </select>
                    </div>

                    {/* Date Fields - Only show for weekly/monthly challenges */}
                    {(formData.category === 'weekly' || formData.category === 'monthly') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Start Date
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                          />
                          {!formData.startDate && (
                            <p className="text-red-500 text-sm mt-1">Start date is required</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            End Date
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                          />
                          {!formData.endDate && (
                            <p className="text-red-500 text-sm mt-1">End date is required</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Daily Challenge Info */}
                    {formData.category === 'daily' && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <p className="font-medium">💡 Daily Challenge:</p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            This challenge will be ongoing with no end date. Users can earn points every day as long as the challenge is active.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Weekly/Monthly Challenge Info */}
                    {(formData.category === 'weekly' || formData.category === 'monthly') && formData.startDate && formData.endDate && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                        <div className="text-sm text-green-800 dark:text-green-200">
                          <p className="font-medium">📅 {formData.category === 'weekly' ? 'Weekly' : 'Monthly'} Challenge:</p>
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Duration: {Math.ceil((new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          How to Track Progress
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                          value={formData.metric}
                          onChange={(e) => handleMetricChange(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                        >
                          <option value="steps">👟 Steps</option>
                          <option value="time">⏱️ Time</option>
                          <option value="selfReported">✍️ Self Reported</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Input Method
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <select
                          value={formData.inputType}
                          onChange={(e) => setFormData({ ...formData, inputType: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                        >
                          <option value="checkbox">☑️ Checkbox</option>
                          <option value="number">🔢 Number</option>
                          <option value="text">📝 Text</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Status
                        </label>
                        <div className="flex items-center space-x-6 pt-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.isActive}
                              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Active Challenge</span>
                          </label>

                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.allowMultipleEntries}
                              onChange={(e) => setFormData({ ...formData, allowMultipleEntries: e.target.checked })}
                              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Allow Multiple Entries</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Challenge Goal & Rewards */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mr-3">
                      🎯
                    </span>
                    Challenge Goal & Rewards
                  </h3>

                  <div className="space-y-4">
                    {/* Daily Goal & Points */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Daily Goal
                          <span className="text-red-500 ml-1">*</span>
                          <span className="ml-2 text-gray-500 text-xs">🎯 What users need to achieve each day</span>
                        </label>
                        <input
                          type="number"
                          value={formData.measurementTarget || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === null || value === undefined) {
                              setFormData(prev => ({ ...prev, measurementTarget: 0 }));
                              return;
                            }
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              handleMeasurementTargetChange(numValue);
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                          placeholder="5000"
                          min="0.1"
                          step="0.1"
                        />
                        {!formData.measurementTarget || formData.measurementTarget < 0.1 && (
                          <p className="text-red-500 text-sm mt-1">Daily goal must be at least 0.1</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Points Per Day
                          <span className="text-red-500 ml-1">*</span>
                          <span className="ml-2 text-gray-500 text-xs">💰 Points earned for completing daily goal</span>
                        </label>
                        <input
                          type="number"
                          value={formData.points || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || value === null || value === undefined) {
                              setFormData(prev => ({ ...prev, points: 0 }));
                              return;
                            }
                            const numValue = parseInt(value);
                            if (!isNaN(numValue)) {
                              handlePointsChange(numValue);
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                          placeholder="10"
                          min="1"
                        />
                        {!formData.points || formData.points < 1 && (
                          <p className="text-red-500 text-sm mt-1">Points per day must be at least 1</p>
                        )}
                      </div>
                    </div>

                    {/* Unit of Measurement */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Unit of Measurement
                        <span className="text-red-500 ml-1">*</span>
                        <span className="ml-2 text-gray-500 text-xs">📏 What to track (steps, fl oz, etc.)</span>
                      </label>
                      <select
                        value={formData.measurementUnit}
                        onChange={(e) => setFormData({ ...formData, measurementUnit: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                      >
                        {getMeasurementUnits().map(unit => (
                          <option key={unit} value={unit}>
                            {unit.charAt(0).toUpperCase() + unit.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Challenge Summary */}
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                      <div className="text-sm text-green-800 dark:text-green-200">
                        <p className="font-medium mb-2">✅ Challenge Summary:</p>
                        <div className="space-y-1">
                          <p>• <strong>Daily Goal:</strong> {formData.measurementTarget} {formData.measurementUnit}</p>
                          <p>• <strong>Daily Reward:</strong> {formData.points} points</p>
                          <p>• <strong>Challenge Type:</strong> {formData.category === 'daily' ? 'Ongoing daily challenge' : formData.category === 'weekly' ? 'Weekly challenge' : formData.category === 'monthly' ? 'Monthly challenge' : ''}</p>
                        </div>

                        {/* Show calculated totals for weekly/monthly challenges */}
                        {(formData.category === 'weekly' || formData.category === 'monthly') && formData.startDate && formData.endDate && (
                          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-600">
                            <div className="grid grid-cols-2 gap-4 text-center">
                              <div>
                                <p className="text-xs text-green-600 dark:text-green-400 mb-1">
                                  {formData.category === 'weekly' ? 'Weekly Goal' : 'Monthly Goal'}
                                </p>
                                <p className="font-semibold text-lg">
                                  {formData.measurementTarget * (formData.category === 'weekly' ? 7 : 30)} {formData.measurementUnit}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-green-600 dark:text-green-400 mb-1">
                                  {formData.category === 'weekly' ? 'Weekly Points' : 'Monthly Points'}
                                </p>
                                <p className="font-semibold text-lg">
                                  {formData.points * (formData.category === 'weekly' ? 7 : 30)} points
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                          💡 {formData.category === 'daily' ? 'Users earn points every day as long as the challenge is active' : `Users work toward ${formData.category === 'weekly' ? 'weekly' : 'monthly'} goals within the specified time period`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Media Content */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <span className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-xl flex items-center justify-center mr-3">
                      🎬
                    </span>
                    Media Content (Optional)
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Video URL
                        <span className="text-gray-500 text-xs ml-2">🎥 Optional - plays when joining</span>
                      </label>
                      <input
                        type="url"
                        value={formData.videoUrl}
                        onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                        placeholder="https://example.com/video.mp4"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Image URL
                        <span className="text-gray-500 text-xs ml-2">🖼️ Optional - challenge banner</span>
                      </label>
                      <input
                        type="url"
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Streak Bonus Configuration */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-lg flex items-center justify-center mr-3">
                    🏆
                  </span>
                  Streak Bonus System
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.streakBonusEnabled}
                        onChange={(e) => setFormData({ ...formData, streakBonusEnabled: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="ml-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Enable Streak Bonuses</span>
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-8">
                      Reward users for consistent participation
                    </p>
                  </div>

                  {formData.streakBonusEnabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Streak Type
                            <span className="text-gray-500 text-xs ml-2">📊 What counts as a streak</span>
                          </label>
                          <select
                            value={formData.streakBonusType}
                            onChange={(e) => setFormData({ ...formData, streakBonusType: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                          >
                            <option value="daily">📅 Daily Streaks</option>
                            <option value="weekly">📊 Weekly Streaks</option>
                            <option value="monthly">📈 Monthly Streaks</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Streak Threshold
                            <span className="text-gray-500 text-xs ml-2">🎯 Consecutive completions needed</span>
                          </label>
                          <input
                            type="number"
                            value={formData.streakBonusThreshold}
                            onChange={(e) => setFormData({ ...formData, streakBonusThreshold: parseInt(e.target.value) })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                            placeholder="7"
                            min="1"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Bonus Points
                            <span className="text-gray-500 text-xs ml-2">🎁 Extra points for streaks</span>
                          </label>
                          <input
                            type="number"
                            value={formData.streakBonusPoints}
                            onChange={(e) => setFormData({ ...formData, streakBonusPoints: parseInt(e.target.value) })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                            placeholder="25"
                            min="0"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Max Bonus
                            <span className="text-gray-500 text-xs ml-2">💰 Maximum bonus points</span>
                          </label>
                          <input
                            type="number"
                            value={formData.maxStreakBonus}
                            onChange={(e) => setFormData({ ...formData, maxStreakBonus: parseInt(e.target.value) })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                            placeholder="100"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Streak Bonus Examples */}
                      <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-700">
                        <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">💡 Streak Bonus Examples</h4>
                        <div className="text-xs text-emerald-700 dark:text-emerald-300 space-y-1">
                          {formData.streakBonusType === 'daily' && (
                            <>
                              <p>• Complete challenge for {formData.streakBonusThreshold} days in a row = +{formData.streakBonusPoints} bonus points</p>
                              <p>• Continue streak for another {formData.streakBonusThreshold} days = +{formData.streakBonusPoints} more bonus points</p>
                              <p>• Maximum bonus: {formData.maxStreakBonus} points</p>
                            </>
                          )}
                          {formData.streakBonusType === 'weekly' && (
                            <>
                              <p>• Complete challenge for {formData.streakBonusThreshold} weeks in a row = +{formData.streakBonusPoints} bonus points</p>
                              <p>• Continue streak for another {formData.streakBonusThreshold} weeks = +{formData.streakBonusPoints} more bonus points</p>
                              <p>• Maximum bonus: {formData.maxStreakBonus} points</p>
                            </>
                          )}
                          {formData.streakBonusType === 'monthly' && (
                            <>
                              <p>• Complete challenge for {formData.streakBonusThreshold} months in a row = +{formData.streakBonusPoints} bonus points</p>
                              <p>• Continue streak for another {formData.streakBonusThreshold} months = +{formData.streakBonusPoints} more bonus points</p>
                              <p>• Maximum bonus: {formData.maxStreakBonus} points</p>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-900 rounded-b-2xl border-t border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="text-red-500">*</span> Required fields
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={showCreateModal ? handleCreateChallenge : handleUpdateChallenge}
                    disabled={!formData.title || !formData.description || !formData.emoji || (formData.level === 'organization' && !formData.organizationId) || !formData.measurementTarget || !formData.measurementUnit}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    {showCreateModal ? 'Create Challenge' : 'Update Challenge'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full transition-colors duration-300">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Delete Challenge</h3>
                  <p className="text-slate-600">This action cannot be undone.</p>
                </div>
              </div>

              <p className="text-slate-700 mb-6">
                Are you sure you want to delete <strong>"{selectedChallenge.title}"</strong>?
                This will permanently remove the challenge and all associated data.
              </p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteChallenge}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Challenge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Challenges; 